import React, { useState, useEffect } from 'react';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Avatar from '@mui/material/Avatar';
import { teal, lightBlue } from '@mui/material/colors';
import CardHeader from '@mui/material/CardHeader'
import { ChatbotResultType, useChatbotMutation } from '../store/services/chatservice';
import { useSelector } from 'react-redux'
import { RootState, useAppDispatch } from '../store'
import { setConversationId } from '../store/features/chatbotSlice'
import Map from '../components/chatbot/Map'
import Report from '../components/chatbot/Report'
import QuestionsDrawer from '../components/chatbot/QuestionsDrawer';
import { useIdToken } from '../hooks/useLogin';
import FaultAlarm from '../components/chatbot/FaultAlarm';
import QuestionInput from '../components/chatbot/QuestionInput';
import ChatHeader from '../components/chatbot/ChatHeader';

const REFRESH_TOKEN_EVERY_X_MINUTES = 20 * 1000 * 60

export interface ChatResponseAnswer {
  message?: string;
  conversation_id?: string;
}

interface ChatResultType {
  query: String,
  answer: ChatResponseAnswer
}

const ChatPage = () => {
  const { getToken } = useIdToken()
  const dispatch = useAppDispatch()
  const [query, setQuery] = useState<string>('Where are my machines?')
  const [chatResults, setChatResults] = useState<ChatResultType[]>([])
  let [chatbot, { isLoading }] = useChatbotMutation<ChatbotResultType>()
  const [openDrawer, setOpenDrawer] = useState(false)
  const email = useSelector((root: RootState) => root.chatbot.email)
  const conversationId = useSelector((root: RootState) => root.chatbot.conversationId)

  const toggleDrawer = () => setOpenDrawer((prev) => !prev)

  const handleSendClick = async (inputValue: string | null) => {
    setQuery('')
    if (!inputValue) return

    chatbot({ conversation_id: conversationId, message: inputValue })
      .then((res: any) => {
        setChatResults(p => [...p, { query: inputValue, answer: res.data }])
        if (res.data?.conversation_id) {
          dispatch(setConversationId(res.data.conversation_id))
        }
      })
      .finally(() => {
        const _questions = localStorage.getItem("questions") || '[]'
        const questions: [string?] = JSON.parse(_questions)
        const _indexFound = questions.findIndex(r => r === inputValue)

        if (_indexFound === -1) questions.push(inputValue)

        localStorage.setItem("questions", JSON.stringify(questions))

        const id = chatResults?.length.toString() || ''

        if (id) {
          setTimeout(() => {
            document.getElementById(id)?.scrollIntoView({
              behavior: "smooth",
              block: "end",
              inline: "nearest"
            });
          }, 2000)
        }
      })
  }

  useEffect(() => {
    getToken()
    const timer = setInterval(async () => {
      await getToken()
    }, REFRESH_TOKEN_EVERY_X_MINUTES);

    return () => {
      clearInterval(timer);
    }
  }, [getToken]);

  return (
    <>
      {/* <ChatHeader /> */}
      <QuestionsDrawer open={openDrawer} toggleDrawer={toggleDrawer} />
      <Grid 
        container 
        component={Paper} 
        direction="column" 
        wrap="nowrap" 
        sx={{ 
          backgroundColor: '#f5f5f5', 
          color: '#fff',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative'
        }}>
        <Grid 
          item 
          xs={12} 
          sx={{ 
            flex: '1 1 auto',
            overflowY: 'auto',
            overflowX: 'hidden',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column'
          }}>
          {chatResults.map((e, index) => (
            <Grid container key={index}>
              <Grid
                item
                xs={12}
                sx={{
                  backgroundColor: '#f5f5f5',
                  color: '#000',
                  textAlign: 'left'
                }}>
                <CardHeader
                  avatar={
                    <Avatar sx={{ bgcolor: teal[100] }}>H</Avatar>
                  }
                  title={e.query}
                  titleTypographyProps={{ fontSize: '1em' }}
                  sx={{ alignItems: 'flex-start' }}
                />
              </Grid>
              <Grid
                item xs={12}
                sx={{
                  backgroundColor: '#f5f5f5',
                  color: '#000',
                  textAlign: 'left'
                }}>
                <CardHeader
                  avatar={
                    <Avatar sx={{ bgcolor: lightBlue['500'] }}>AI</Avatar>
                  }
                  title={
                    !!e.answer.message ? (
                      <div id={index.toString()} dangerouslySetInnerHTML={{ __html: e.answer.message as string }} />
                    ) : ''
                  }
                  titleTypographyProps={{ fontSize: '1em' }}
                  sx={{ alignItems: 'flex-start' }}
                />
              </Grid>
            </Grid>
          ))}
        </Grid>
        <Grid 
          item 
          xs={12}
          sx={{ 
            flexShrink: 0,
            backgroundColor: '#f5f5f5',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000
          }}>
          <QuestionInput
            isLoading={isLoading}
            query={query}
            handleSendClick={handleSendClick} />
        </Grid>
      </Grid>
    </>
  );
}


export default ChatPage;